import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { LogOut, Users, Send, Rocket, MessageSquare, User, TrendingUp, TrendingDown, Minus, BarChart3, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CoachDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [suggestion, setSuggestion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Market Sentiment State
  const [sentiment, setSentiment] = useState(null);
  const [loadingSentiment, setLoadingSentiment] = useState(false);

  useEffect(() => {
    if (user.role !== 'coach') {
      navigate('/dashboard');
      return;
    }
    loadRequests();
  }, [user, navigate]);

  const loadRequests = async () => {
    try {
      const response = await axios.get(`${API}/coach/requests`, { withCredentials: true });
      setRequests(response.data);
    } catch (error) {
      toast.error('Failed to load coaching requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitSuggestion = async () => {
    if (!suggestion.trim()) {
      toast.error('Please enter a suggestion');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/coach/suggestions`,
        {
          request_id: selectedRequest.id,
          suggestion_text: suggestion
        },
        { withCredentials: true }
      );
      
      toast.success('Suggestion submitted successfully!');
      setSuggestion('');
      setSelectedRequest(null);
      setSentiment(null);
      loadRequests();
    } catch (error) {
      toast.error('Failed to submit suggestion');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGetSentiment = async () => {
    if (!selectedRequest) return;
    
    setLoadingSentiment(true);
    setSentiment(null);
    
    try {
      const response = await axios.get(
        `${API}/coach/market-sentiment/${selectedRequest.startup_id}`,
        { withCredentials: true }
      );
      setSentiment(response.data);
      toast.success('Market sentiment analysis complete!');
    } catch (error) {
      toast.error('Failed to analyze market sentiment');
      console.error('Sentiment error:', error);
    } finally {
      setLoadingSentiment(false);
    }
  };

  const getSentimentIcon = (score) => {
    if (score === 'Positive') return <TrendingUp className="w-6 h-6 text-green-400" />;
    if (score === 'Negative') return <TrendingDown className="w-6 h-6 text-red-400" />;
    return <Minus className="w-6 h-6 text-amber-400" />;
  };

  const getSentimentColor = (score) => {
    if (score === 'Positive') return 'from-green-500 to-emerald-600';
    if (score === 'Negative') return 'from-red-500 to-rose-600';
    return 'from-amber-500 to-orange-600';
  };

  const getSentimentBgColor = (score) => {
    if (score === 'Positive') return 'bg-green-500/10 border-green-500/30';
    if (score === 'Negative') return 'bg-red-500/10 border-red-500/30';
    return 'bg-amber-500/10 border-amber-500/30';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" data-testid="coach-dashboard">
      {/* Header */}
      <nav className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-9 h-9 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                <Rocket className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                StartupBlackbox
              </span>
            </div>
            <span className="px-3 py-1 bg-green-500/10 text-green-400 text-sm rounded-full border border-green-500/30">
              Coach Panel
            </span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-slate-300">{user.name}</span>
            <Button
              variant="ghost"
              onClick={onLogout}
              data-testid="coach-logout-btn"
              className="text-slate-400 hover:text-red-400"
              type="button"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-slate-800/40 border-slate-700 p-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Pending Requests</p>
                <p className="text-2xl font-bold text-slate-100">{requests.length}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Requests List */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Request List */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-slate-100 mb-4">Coaching Requests</h2>
            
            {requests.length === 0 ? (
              <Card className="bg-slate-800/40 border-slate-700 p-12">
                <div className="text-center">
                  <Users className="w-16 h-16 mx-auto mb-4 text-slate-600" />
                  <h3 className="text-lg font-semibold text-slate-300 mb-2">No Pending Requests</h3>
                  <p className="text-slate-400">Check back soon for new coaching requests!</p>
                </div>
              </Card>
            ) : (
              requests.map((request) => (
                <Card
                  key={request.id}
                  className={`bg-slate-800/40 border-slate-700 p-6 cursor-pointer transition-all ${
                    selectedRequest?.id === request.id ? 'border-cyan-500 shadow-lg shadow-cyan-500/20' : 'hover:border-slate-600'
                  }`}
                  onClick={() => {
                    setSelectedRequest(request);
                    setSentiment(null);
                  }}
                  data-testid={`request-${request.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-100">{request.startup_name}</h4>
                        <p className="text-sm text-slate-400">{request.user_name}</p>
                      </div>
                    </div>
                    <span className="px-2 py-1 bg-amber-500/10 text-amber-400 text-xs rounded-full border border-amber-500/30">
                      {request.request_type}
                    </span>
                  </div>
                  
                  <div className="text-sm text-slate-400 mb-2">
                    <p>Email: {request.user_email}</p>
                  </div>

                  {request.goal_data && (
                    <div className="mt-3 p-3 bg-slate-700/30 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">Goal Details:</p>
                      <pre className="text-sm text-slate-300 whitespace-pre-wrap">
                        {JSON.stringify(request.goal_data, null, 2)}
                      </pre>
                    </div>
                  )}

                  <p className="text-xs text-slate-500 mt-3">
                    Requested: {new Date(request.created_at).toLocaleString()}
                  </p>
                </Card>
              ))
            )}
          </div>

          {/* Right: Suggestion Form + Sentiment */}
          <div className="lg:sticky lg:top-24 h-fit space-y-6">
            {selectedRequest ? (
              <>
                {/* Market Sentiment Card */}
                <Card className="bg-slate-800/40 border-slate-700 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-slate-100 flex items-center">
                      <BarChart3 className="w-5 h-5 mr-2 text-purple-400" />
                      Market Sentiment
                    </h3>
                    <Button
                      onClick={handleGetSentiment}
                      disabled={loadingSentiment}
                      data-testid="analyze-sentiment-btn"
                      size="sm"
                      className="bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700"
                    >
                      {loadingSentiment ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <TrendingUp className="w-4 h-4 mr-2" />
                          Analyze Market
                        </>
                      )}
                    </Button>
                  </div>

                  {sentiment ? (
                    <div className="space-y-4">
                      {/* Sentiment Score */}
                      <div className={`p-4 rounded-lg border ${getSentimentBgColor(sentiment.sentiment_score)}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            {getSentimentIcon(sentiment.sentiment_score)}
                            <span className="text-lg font-bold text-slate-100">{sentiment.sentiment_score}</span>
                          </div>
                          <div className={`text-2xl font-bold bg-gradient-to-r ${getSentimentColor(sentiment.sentiment_score)} bg-clip-text text-transparent`}>
                            {sentiment.sentiment_percentage}%
                          </div>
                        </div>
                        <p className="text-sm text-slate-400">
                          Industry: <span className="text-slate-200">{sentiment.industry}</span>
                        </p>
                      </div>

                      {/* Analysis */}
                      <div>
                        <h4 className="text-sm font-semibold text-slate-300 mb-2">Analysis</h4>
                        <p className="text-slate-400 text-sm leading-relaxed">{sentiment.analysis}</p>
                      </div>

                      {/* Key Factors */}
                      {sentiment.key_factors && sentiment.key_factors.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-slate-300 mb-2">Key Factors</h4>
                          <ul className="space-y-2">
                            {sentiment.key_factors.map((factor, idx) => (
                              <li key={idx} className="flex items-start space-x-2 text-sm text-slate-400">
                                <span className="text-purple-400 mt-1">•</span>
                                <span>{factor}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <p className="text-xs text-slate-500 pt-2 border-t border-slate-700">
                        Generated: {new Date(sentiment.generated_at).toLocaleString()}
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <BarChart3 className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                      <p className="text-slate-400 text-sm">
                        Click "Analyze Market" to get AI-powered market sentiment for this startup's industry
                      </p>
                    </div>
                  )}
                </Card>

                {/* Suggestion Form */}
                <Card className="bg-slate-800/40 border-slate-700 p-6">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4">
                    Submit Coaching Suggestion
                  </h3>
                  
                  <div className="mb-4 p-4 bg-slate-700/30 rounded-lg">
                    <p className="text-sm text-slate-400 mb-1">For:</p>
                    <p className="font-semibold text-slate-100">{selectedRequest.startup_name}</p>
                    <p className="text-sm text-slate-300">{selectedRequest.user_name}</p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label className="text-slate-300">Your Suggestion / Action Plan</Label>
                      <Textarea
                        value={suggestion}
                        onChange={(e) => setSuggestion(e.target.value)}
                        data-testid="suggestion-textarea"
                        className="mt-1 bg-slate-800 border-slate-700 text-slate-100 min-h-[200px]"
                        placeholder="Provide detailed coaching suggestions and action steps..."
                      />
                    </div>

                    <Button
                      onClick={handleSubmitSuggestion}
                      disabled={submitting}
                      data-testid="submit-suggestion-btn"
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {submitting ? 'Submitting...' : 'Submit Suggestion'}
                    </Button>
                  </div>
                </Card>
              </>
            ) : (
              <Card className="bg-slate-800/40 border-slate-700 p-12">
                <div className="text-center">
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 text-slate-600" />
                  <h3 className="text-lg font-semibold text-slate-300 mb-2">Select a Request</h3>
                  <p className="text-slate-400">Click on a request to provide coaching suggestions</p>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachDashboard;
